import { AbstractFieldGroupView } from '../base/AbstractFieldGroupView';
import { IVisibleController, VisibleController } from '../../../../../shared/util/VisibleController';
import { DateInputView } from '../../../../../renderApp/components/cells/BaseInput/views/DateInputView';
import { BaseInputFactory } from '../../../../../renderApp/components/cells/BaseInput/BaseInputFactory';
import { limited_identifier } from '../../../../features/ProductEditor/const/limited';
import klass from './SaleStartDateInputField.module.scss';
import input_klass from '../../../../../renderApp/components/cells/DateInputField/styles/BaseInputView.module.scss';
import { html, type Html, View } from 'rune-ts';
import { font } from '../../../../../shared/typography';
import { head, isUndefined } from '@fxts/core';
import { onCalendarChangeEvent } from '../../../../../renderApp/components/cells/DateInputField/DateInputCalendarController';
import { IValidation, ProductEditorError } from '../types/type';

export class SaleEndDateInputField
  extends AbstractFieldGroupView<Date | undefined>
  implements IVisibleController, IValidation
{
  private readonly inputView: DateInputView;
  private visibleController: VisibleController;

  private error_message?: string;

  constructor({ value }: { value: Date | undefined }) {
    const visible = !!value;

    super(value, '한정 판매 종료일', true, undefined, visible);

    this.inputView = BaseInputFactory.create(
      'date',
      value,
      {
        name: limited_identifier.period,
        format: 'yyyy-MM-dd HH:mm:ss',
        placeholder: '종료일 선택',
      },
      {
        klass: `${input_klass.input} ${font('14_regular')}`,
      },
    );
    this.visibleController = new VisibleController(this, visible);
  }

  protected body(): Html | View {
    return html`<div class="${klass.sale_date_field}">
      ${this.inputView}
      <span class="${klass.error_message} ${this.isErrorMessageVisible() ? '' : klass.hidden}">
        ${this.error_message}
      </span>
    </div>`;
  }

  public setErrorMessage(message?: string): this {
    this.error_message = message;
    this.syncErrorMessage();
    return this;
  }

  private isErrorMessageVisible(): boolean {
    return !!this.error_message;
  }

  public getValue(): Date | undefined {
    return this.inputView.getValue();
  }

  public clear(): this {
    this.inputView.setValueAndSync(undefined);
    return this;
  }

  public setVisible(visible: boolean): this {
    if (visible) {
      this.show();
    }
    this.visibleController.setVisible(visible);
    return this;
  }

  public isVisible(): boolean {
    return this.visibleController.isVisible();
  }

  public validate(): ProductEditorError[] {
    const errors = [];

    if (this.isRequired() && this.isVisible() && isUndefined(this.inputView.getValue())) {
      errors.push({
        target: this,
        title: '판매 종료일 미선택',
        message: '종료일을 선택해 주세요.',
        detail: '판매 종료일이 설정되지 않았어요.\n판매 종료일을 선택해야 등록할 수 있어요.',
      });
    }
    return errors;
  }

  private syncErrorMessage() {
    const error_el = this.element().querySelector(`.${klass.error_message}`);
    if (error_el) {
      (error_el as HTMLElement).innerText = this.error_message || '';
      this.inputView.setError(this.isErrorMessageVisible());
      if (this.isErrorMessageVisible()) {
        error_el.classList.remove(klass.hidden);
      } else {
        error_el.classList.add(klass.hidden);
      }
    }
  }

  public showValidation() {
    const first_error = head(this.validate());
    this.setErrorMessage(first_error?.message);
    return this;
  }

  protected override onRender() {
    this.delegate(onCalendarChangeEvent, DateInputView, () => {
      this.showValidation();
    });
  }
}
